<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import { releaseGet, releasePatch } from "@/common/api/rest/release";

import type { TranslationFieldsType } from "@/common/types/translation";
import type { ReleaseType } from "@/common/types/release";

import { FormRelease, ListTranslate } from "@/components";
import { breadcrumbSimple, svgIcon } from "@/common/components";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const releaseData = ref<ReleaseType | null>(null);

const showAvatarDel = ref(false);

const releaseId = computed(() => route.params.release as string);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "release",
    label: t("release.labels.releases"),
    url: `/register/release`,
  },
  {
    key: "edit",
    label: releaseData.value?.name ?? "...",
  },
]);

function back() {
  router.push("/register/release");
}

// release
function fetchData() {
  releaseGet(releaseId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        releaseData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<ReleaseType>) {
  releasePatch(releaseId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="no-image" size="2rem" />
          {{ t("release.labels.edit_release") }}
        </div>

        <div class="actions">
          <button class="btn btn-default" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <form-release
          :data="releaseData"
          :is-patch="true"
          @update:data="patchData($event)"
        />

        <list-translate
          :owner-id="releaseId"
          :language="locale.toLowerCase()"
          :fields="translationFields"
        />
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
